import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Wallet,
  FileText,
  BarChart3,
  Gift,
  Shield,
  CheckCircle,
  Calendar,
  Phone
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function FirstTimeBuyerGuide() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#020617] text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        <Link to="/bio/zahirul" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Profile
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Home className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">🏡 First-Time Buyer Guidance</h1>
          <p className="text-slate-400">Thames Mortgage & Protection</p>
          <p className="text-lg text-blue-400 mt-4">Buying your first home is a major financial decision.</p>
          <p className="text-slate-300">We provide clear, structured advice — tailored to you.</p>
        </motion.div>

        {/* Deposit Strategy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold">💼 Deposit Strategy</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Minimum deposit typically 5%–10%</li>
            <li>• Larger deposits unlock more competitive rates</li>
            <li>• Gifted deposits accepted (subject to lender criteria)</li>
            <li>• Clear source of funds required for compliance</li>
          </ul>
          <p className="text-blue-400 mt-4 font-medium">We help you structure your deposit correctly from day one.</p>
        </motion.div>

        {/* Stamp Duty */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-xl font-semibold">🧾 Stamp Duty Planning (England)</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• First-Time Buyer relief may apply</li>
            <li>• Reduced rates within qualifying thresholds</li>
            <li>• Legal fees, valuation & moving costs must be factored in</li>
          </ul>
          <p className="text-purple-400 mt-4 font-medium">We ensure there are no surprises at completion.</p>
        </motion.div>

        {/* Borrowing Power */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold">📊 Borrowing Power (Income Multiples)</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Standard lending up to 4.5x income</li>
            <li>• Selected lenders offer 5x–5.5x (subject to profile)</li>
            <li>• Affordability, credit conduct & commitments assessed</li>
            <li>• Self-employed, bonus & overtime reviewed carefully</li>
          </ul>
          <p className="text-green-400 mt-4 font-medium">We match your profile to the right lender — not just the headline rate.</p>
        </motion.div>

        {/* Gifted Deposit */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Gift className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold">🎁 Gifted Deposit Guidance</h2>
          </div>
          <ul className="space-y-2 text-slate-300">
            <li>• Usually from immediate family</li>
            <li>• Must be non-repayable</li>
            <li>• Formal declaration required</li>
            <li>• AML & ID verification applies</li>
          </ul>
          <p className="text-amber-400 mt-4 font-medium">Handled smoothly, professionally and fully compliant.</p>
        </motion.div>

        {/* Secure Your Position */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur border border-blue-500/30 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold">Secure Your Position Early</h2>
          </div>
          <p className="text-slate-300 mb-4">Before viewing properties, know:</p>
          <ul className="space-y-2 text-slate-300">
            <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-400" /> How much you can borrow</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-400" /> What your monthly payments look like</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-400" /> Which lenders suit your profile</li>
            <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-400" /> How to position your application strongly</li>
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur border border-blue-500/30 rounded-2xl p-6 mb-6 text-center"
        >
          <h2 className="text-2xl font-bold mb-2">Book Your Private First-Time Buyer Consultation</h2>
          <p className="text-slate-300 mb-4">Personal, adviser-led guidance from start to completion.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="tel:07550199399" className="block">
              <Button className="w-full h-14 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
            <Link to="/book-appointment" className="block">
              <Button className="w-full h-14 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg">
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Your Consultation Now
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition text-sm">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-3 h-3 text-white" />
            </div>
            Thames Mortgage & Protection
          </Link>
          <p className="text-xs text-slate-500 mt-3">FCA Regulated • FRN 1045227</p>
          <p className="text-xs text-amber-500 mt-2 font-medium">Your home may be repossessed if you do not keep up repayments on your mortgage or loans secured on it.</p>
        </motion.div>
      </div>
    </div>
  );
}
